import React, { createContext, useContext, useState, ReactNode, useEffect } from 'react';

export type LicenseEndpointOption = 'auto' | 'arcus' | 'playfactory';

interface EndpointSelections {
	[instanceId: string]: LicenseEndpointOption;
}

interface LicenseEndpointContextType {
	getSelectedEndpoint: (instanceId: string) => LicenseEndpointOption;
	setSelectedEndpoint: (instanceId: string, option: LicenseEndpointOption) => void;
}

const LicenseEndpointContext = createContext<LicenseEndpointContextType | undefined>(undefined);

const STORAGE_KEY = 'licenseEndpointSelections';

export const LicenseEndpointProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
	const [selections, setSelections] = useState<EndpointSelections>(() => {
		// Load from localStorage on init
		try {
			const stored = localStorage.getItem(STORAGE_KEY);
			return stored ? JSON.parse(stored) : {};
		} catch {
			return {};
		}
	});

	// Save to localStorage whenever selections change
	useEffect(() => {
		try {
			localStorage.setItem(STORAGE_KEY, JSON.stringify(selections));
		} catch {
			// Ignore storage errors
		}
	}, [selections]);

	const getSelectedEndpoint = (instanceId: string): LicenseEndpointOption => {
		return selections[instanceId] || 'auto';
	};

	const setSelectedEndpoint = (instanceId: string, option: LicenseEndpointOption): void => {
		setSelections((prev) => ({
			...prev,
			[instanceId]: option
		}));
	};

	return (
		<LicenseEndpointContext.Provider value={{ getSelectedEndpoint, setSelectedEndpoint }}>
			{children}
		</LicenseEndpointContext.Provider>
	);
};

export const useLicenseEndpoint = (instanceId: string = 'global'): { selectedEndpoint: LicenseEndpointOption; setSelectedEndpoint: (option: LicenseEndpointOption) => void } => {
	const context = useContext(LicenseEndpointContext);
	if (!context) {
		throw new Error('useLicenseEndpoint must be used within a LicenseEndpointProvider');
	}

	return {
		selectedEndpoint: context.getSelectedEndpoint(instanceId),
		setSelectedEndpoint: (option: LicenseEndpointOption) => context.setSelectedEndpoint(instanceId, option)
	};
};
